/* ============================================================
   Complementary Products — SKU-baseret via /search/suggest.json
   Slår SKUer op → henter produktdata → renderer items dynamisk
   ============================================================ */

class ComplementaryProductsSku extends HTMLElement {
  connectedCallback() {
    const rawSkus = (this.dataset.skus || '').split(',').map(s => s.trim()).filter(Boolean);
    if (!rawSkus.length) return;

    this.max        = parseInt(this.dataset.max, 10) || 4;
    this.addLabel   = this.dataset.addLabel   || 'Føj til indkøbskurv';
    this.optLabel   = this.dataset.optionsLabel || 'Vælg variant';
    this.list       = this.querySelector('.complementary-products__list');
    this.template   = this.querySelector('template');
    this.sectionId  = this.dataset.sectionId;

    const skus = rawSkus.slice(0, this.max);
    this.renderSkeletons(skus.length);
    this.fetchAndRender(skus);
  }

  /* ---- Skeletons mens vi venter ---- */
  renderSkeletons(count) {
    for (let i = 0; i < count; i++) {
      const li = this.cloneTemplate();
      li.classList.add('complementary-products__item--skeleton');
      // Giv skeleton-felterne en min-bredde så de er synlige
      li.querySelector('.complementary-products__title').textContent = '\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0';
      li.querySelector('.complementary-products__price--current').textContent = '\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0';
      li.querySelector('.complementary-products__add-btn').textContent = '\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0';
      this.list.appendChild(li);
    }
  }

  /* ---- Hent produkter for alle SKUer parallelt ---- */
  async fetchAndRender(skus) {
    const results = await Promise.all(skus.map(sku => this.fetchBySku(sku)));
    this.list.innerHTML = '';

    results.forEach(product => {
      if (!product) return;
      const li = this.buildItem(product);
      this.list.appendChild(li);
    });

    this.initForms();
  }

  /* ---- Søg efter produkt via SKU med /search/suggest.json ---- */
  async fetchBySku(sku) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[limit]=5&resources[fields]=title,variants.sku`;
      const res  = await fetch(url);
      const data = await res.json();
      const hits = data?.resources?.results?.products || [];

      // Find det produkt, der har en variant med præcis denne SKU
      for (const hit of hits) {
        const detail = await this.fetchProductDetail(hit.handle);
        if (!detail) continue;
        const variant = detail.variants.find(v => v.sku === sku);
        if (variant) return { ...detail, matchedVariant: variant };
      }

      // Fallback: returner første hit hvis ingen eksakt SKU-match
      if (hits[0]) {
        const detail = await this.fetchProductDetail(hits[0].handle);
        if (detail) return { ...detail, matchedVariant: detail.variants[0] };
      }
    } catch (e) {
      console.warn(`[ComplementaryProducts] SKU lookup failed for "${sku}":`, e);
    }
    return null;
  }

  /* ---- Hent fuld produktdata inkl. varianter ---- */
  async fetchProductDetail(handle) {
    try {
      const res  = await fetch(`/products/${handle}.js`);
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  /* ---- Byg et list-item fra produktdata ---- */
  buildItem(product) {
    const li       = this.cloneTemplate();
    const variant  = product.matchedVariant || product.variants[0];
    const url      = `/products/${product.handle}`;
    const hasOptions = product.variants.length > 1 || product.options?.[0] !== 'Title';

    // Billede
    const imgLink = li.querySelector('.complementary-products__image-link');
    const img     = li.querySelector('.complementary-products__image');
    const imgSrc  = variant.featured_image?.src || product.featured_image;
    imgLink.href  = url;
    if (imgSrc) {
      // Shopify CDN: brug 200x200 crop
      img.src = imgSrc.replace(/(\.[a-z]+)(\?.*)?$/i, '_200x200$1$2');
      img.alt = product.title;
    } else {
      img.remove();
    }

    // Titel
    const titleLink  = li.querySelector('.complementary-products__title');
    titleLink.href   = url;
    titleLink.textContent = product.title;

    // Pris
    const priceEl   = li.querySelector('.complementary-products__price--current');
    const compareEl = li.querySelector('.complementary-products__price--compare');
    priceEl.textContent = this.formatMoney(variant.price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareEl.textContent = this.formatMoney(variant.compare_at_price);
      compareEl.hidden      = false;
      priceEl.classList.add('complementary-products__price--sale');
    }

    // Handling: tilgængelighed
    const form        = li.querySelector('form');
    const addBtn      = li.querySelector('.complementary-products__add-btn');
    const optLink     = li.querySelector('.complementary-products__options-link');
    const soldOutEl   = li.querySelector('.complementary-products__sold-out');

    if (!product.available) {
      form.hidden      = true;
      optLink.hidden   = true;
      soldOutEl.hidden = false;
    } else if (hasOptions) {
      // Produkt med varianter → link til produktside
      form.hidden      = true;
      optLink.href     = url;
      optLink.textContent = this.optLabel;
      optLink.hidden   = false;
    } else {
      // Enkelt variant → direkte add-to-cart
      li.querySelector('input[name="id"]').value = variant.id;
      addBtn.textContent = this.addLabel;
      addBtn.setAttribute('aria-label', `${this.addLabel}: ${product.title}`);
    }

    return li;
  }

  /* ---- Klon template-elementet ---- */
  cloneTemplate() {
    return this.template.content.cloneNode(true).querySelector('li');
  }

  /* ---- Bind add-to-cart forms ---- */
  initForms() {
    this.querySelectorAll('.complementary-products__item form').forEach(form => {
      form.addEventListener('submit', e => {
        e.preventDefault();
        this.addToCart(form);
      });
    });
  }

  async addToCart(form) {
    const btn = form.querySelector('[type="submit"]');
    const id  = form.querySelector('[name="id"]').value;
    if (!id) return;

    btn.disabled = true;
    btn.classList.add('loading');

    try {
      const res  = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(id, 10), quantity: 1 }),
      });

      if (!res.ok) throw new Error(res.statusText);

      btn.classList.remove('loading');
      btn.classList.add('success');

      // Notify theme om cart-opdatering (Dawn-kompatibelt + fallback events)
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

      // Prøv at refreshe cart drawer via Dawn's event system
      const cartUpdateEvent = new CustomEvent('product-added-to-cart', {
        bubbles: true,
        detail: { value: await res.clone().json() }
      });
      document.dispatchEvent(cartUpdateEvent);

      // Opdater cart count hvis tema bruger fetch cart
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          document.querySelectorAll('[data-cart-count], .cart-count-bubble span:first-child, #cart-icon-bubble span').forEach(el => {
            el.textContent = cart.item_count;
          });
        });

      setTimeout(() => {
        btn.classList.remove('success');
        btn.disabled = false;
      }, 1800);

    } catch (err) {
      console.warn('[ComplementaryProducts] Add to cart error:', err);
      btn.disabled = false;
      btn.classList.remove('loading');
    }
  }

  /* ---- Shopify money formatter — bruger butikkens format fra window.themeVariables ---- */
  formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';

    const settings = window.themeVariables?.settings || {};
    const format = (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat)
      || '{{amount_no_decimals_with_comma_separator}} kr';

    const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (number / 100).toFixed(precision).split('.');
      const wholeWithDelimiters = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${wholeWithDelimiters}${decimal}${fraction}` : wholeWithDelimiters;
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':                                   return formatWithDelimiters(cents, 2);
        case 'amount_no_decimals':                       return formatWithDelimiters(cents, 0);
        case 'amount_with_comma_separator':              return formatWithDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':  return formatWithDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':         return formatWithDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':  return formatWithDelimiters(cents, 0, ' ');
        case 'amount_with_space_separator':              return formatWithDelimiters(cents, 2, ' ', ',');
        case 'amount_with_period_and_space_separator':   return formatWithDelimiters(cents, 2, ' ', '.');
        default:                                         return formatWithDelimiters(cents, 2);
      }
    });
  }
}

customElements.define('complementary-products-sku', ComplementaryProductsSku);